import React, { useState, useEffect, useCallback } from "react";
import { MappingItem } from "./Models";

interface EmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (emailData: { to: string; subject: string; body: string }) => void;
  tenantEmail: string;
  tenantName: string;
  unmappedMembers: MappingItem[];
  mappedMembers: MappingItem[];
  loading?: boolean;
}

const EmailModal: React.FC<EmailModalProps> = ({
  isOpen,
  onClose,
  onSend,
  tenantEmail,
  tenantName,
  unmappedMembers,
  mappedMembers,
  loading = false,
}) => {
  const [subject, setSubject] = useState("Missing Application Submission");
  const [emailBody, setEmailBody] = useState("");
  const [validationError, setValidationError] = useState("");

  const generateTemplate = useCallback(() => {
    const unmappedList = unmappedMembers
      .map(
        (member) =>
          `• ${member.name} (${member.isGuarantor ? "Guarantor" : "Tenant"})`,
      )
      .join("\n");

    const mappedList = mappedMembers
      .map(
        (member) =>
          `• ${member.name} (${member.isGuarantor ? "Guarantor" : "Tenant"})`,
      )
      .join("\n");

    const sections = [
      `Dear ${tenantName},`,
      `I hope you are doing well. I am writing to share the current AppFolio application status for your household.`,
    ];

    if (unmappedMembers.length > 0) {
      sections.push(
        `Applications Still Pending:`,
        `The following members have not yet submitted their AppFolio applications:`,
        unmappedList,
        `Please ask them to complete and submit their applications at their earliest convenience so we can continue with the next steps.`,
      );
    }

    if (mappedMembers.length > 0) {
      sections.push(
        `Applications Received and Processed:`,
        `The following members have already submitted applications that have been received and processed:`,
        mappedList,
      );
    }

    sections.push(
      unmappedMembers.length > 0
        ? `Please follow up with the remaining candidates when convenient. If you need any assistance, please feel free to reach out.`
        : `All required applications have now been received and processed.`,
      `Kind regards,`,
      `The Downtown Apartments, LLC`,
    );

    return sections.filter((section) => section.trim().length > 0).join("\n\n");
  }, [mappedMembers, tenantName, unmappedMembers]);

  useEffect(() => {
    if (isOpen) {
      setValidationError("");
      setEmailBody(generateTemplate());
    }
  }, [generateTemplate, isOpen]);

  const handleSend = () => {
    if (!subject.trim() || !emailBody.trim()) {
      setValidationError("Subject and message are required before sending.");
      return;
    }

    setValidationError("");

    onSend({
      to: tenantEmail,
      subject: subject,
      body: emailBody,
    });
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden relative flex flex-col">
        {loading && (
          <div className="absolute inset-0 bg-gray-200 bg-opacity-75 flex items-center justify-center z-10">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="text-gray-700 font-medium">
                Sending email...
              </span>
            </div>
          </div>
        )}

        <div className="px-6 py-5 border-b border-gray-200">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Send Email Notification
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Review the message before sending it to the tenant group.
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className={`text-gray-400 hover:text-gray-600 ${
                loading ? "cursor-not-allowed opacity-50" : ""
              }`}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="space-y-5">
            {validationError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {validationError}
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                To:
              </label>
              <div className="w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-700">
                {tenantEmail}
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Subject:
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={loading}
                className={`w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  loading ? "bg-gray-50 text-gray-500 cursor-not-allowed" : ""
                }`}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Email Body:
              </label>
              <textarea
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                rows={13}
                disabled={loading}
                className={`w-full rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-[15px] leading-7 text-slate-800 shadow-sm outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100 resize-vertical placeholder:text-slate-400 ${
                  loading
                    ? "cursor-not-allowed bg-slate-100 text-slate-500"
                    : ""
                }`}
                placeholder="Compose a professional email..."
              />
              <div className="text-xs text-slate-500">
                Write the message you want the tenant group to receive. Keep it
                clear, polite, and concise.
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm space-y-2">
              <div className="font-medium text-slate-700">Summary:</div>
              <div className="text-slate-600">
                {unmappedMembers.length > 0 && (
                  <div>
                    • {unmappedMembers.length} member
                    {unmappedMembers.length > 1 ? "s" : ""} without applications
                  </div>
                )}
                {mappedMembers.length > 0 && (
                  <div>
                    • {mappedMembers.length} member
                    {mappedMembers.length > 1 ? "s" : ""} with processed
                    applications
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 bg-white">
          <button
            onClick={handleClose}
            disabled={loading}
            className={`px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium ${
              loading ? "cursor-not-allowed opacity-50" : ""
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={loading || !subject.trim() || !emailBody.trim()}
            className={`px-6 py-2 rounded-lg font-medium ${
              loading || !subject.trim() || !emailBody.trim()
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 text-white"
            }`}
          >
            {loading ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Sending...</span>
              </div>
            ) : (
              "Send Email"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailModal;
